import { db } from './db';
import { api } from './api';

const GUARD = 'titan.guard';
const enc = new TextEncoder();
const b64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const unb64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
const rand = (n) => crypto.getRandomValues(new Uint8Array(n));

async function pbkdf2(pin, salt) {
  const key = await crypto.subtle.importKey('raw', enc.encode(pin), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits({ name: 'PBKDF2', salt, iterations: 210000, hash: 'SHA-256' }, key, 256);
  return b64(bits);
}

export const guard = {
  get: () => { try { return JSON.parse(localStorage.getItem(GUARD)); } catch { return null; } },
  save: (g) => localStorage.setItem(GUARD, JSON.stringify(g)),
  async setup(pin) {
    const salt = rand(16);
    const g = { salt: b64(salt), hash: await pbkdf2(pin, salt), created: Date.now(), fails: 0, lockUntil: 0 };
    this.save(g);
  },
  async sync(pin) {
    const salt = rand(16);
    this.save({ ...(this.get() || { created: Date.now() }), salt: b64(salt), hash: await pbkdf2(pin, salt), fails: 0, lockUntil: 0 });
  },
  async verify(pin) {
    const g = this.get();
    if (!g) return false;
    if (Date.now() < (g.lockUntil || 0)) return 'locked';
    const ok = (await pbkdf2(pin, unb64(g.salt))) === g.hash;
    g.fails = ok ? 0 : (g.fails || 0) + 1;
    if (g.fails >= 5) { g.lockUntil = Date.now() + 30000; g.fails = 0; }
    this.save(g);
    return ok;
  },
};

export async function biometricAvailable() {
  try {
    return !!(window.PublicKeyCredential && (await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()));
  } catch { return false; }
}

const fromB64u = (s) => unb64(s.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (s.length % 4)) % 4));
const toB64u = (b) => b64(b).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
const serialize = (c) => {
  const r = c.response;
  const out = { clientDataJSON: toB64u(r.clientDataJSON) };
  ['attestationObject', 'authenticatorData', 'signature', 'userHandle'].forEach((k) => { if (r[k]) out[k] = toB64u(r[k]); });
  if (r.getTransports) out.transports = r.getTransports();
  return { id: c.id, rawId: toB64u(c.rawId), type: c.type, response: out, clientExtensionResults: c.getClientExtensionResults(), authenticatorAttachment: c.authenticatorAttachment || undefined };
};
const PK = 'nexora.passkey';
export const localPasskey = { get: () => localStorage.getItem(PK), set: (v) => (v ? localStorage.setItem(PK, v) : localStorage.removeItem(PK)) };

export async function registerBiometric() {
  const o = await api('/dev/passkey/register/options', { method: 'POST', auth: true });
  const pk = o.publicKey;
  pk.challenge = fromB64u(pk.challenge);
  pk.user.id = fromB64u(pk.user.id);
  (pk.excludeCredentials || []).forEach((c) => { c.id = fromB64u(c.id); });
  const cred = await navigator.credentials.create({ publicKey: pk });
  const label = /iPhone|iPad/.test(navigator.userAgent) ? 'iPhone / iPad' : /Android/.test(navigator.userAgent) ? 'Android' : /Mac/.test(navigator.userAgent) ? 'Mac' : 'Dispositivo';
  const r = await api('/dev/passkey/register/verify', { method: 'POST', auth: true, body: { challenge_id: o.challenge_id, credential: serialize(cred), label } });
  localPasskey.set(r.credential_id);
  return true;
}

export async function loginBiometric() {
  const o = await api('/dev/passkey/login/options', { method: 'POST' });
  const pk = o.publicKey;
  pk.challenge = fromB64u(pk.challenge);
  (pk.allowCredentials || []).forEach((c) => { c.id = fromB64u(c.id); });
  const a = await navigator.credentials.get({ publicKey: pk });
  const r = await api('/dev/passkey/login/verify', { method: 'POST', body: { challenge_id: o.challenge_id, credential: serialize(a) } });
  localPasskey.set(a.id);
  return r.token;
}

export const passkeyStatus = () => api('/dev/passkey/status', { method: 'POST', timeout: 6000 }).then((r) => r.enabled).catch(() => false);
export const listPasskeys = () => api('/dev/passkey/list', { method: 'POST', auth: true });
export async function removeBiometric(id) {
  await api(`/dev/passkey/${id}`, { method: 'DELETE', auth: true });
  if (localPasskey.get() === id) localPasskey.set(null);
}

// Device-bound, non-extractable AES key stored in IndexedDB encrypts settings at rest
async function deviceKey() {
  let k = await db.kvGet('deviceKey');
  if (!k) {
    k = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
    await db.kvSet('deviceKey', k);
  }
  return k;
}

export async function saveSecure(name, obj) {
  const iv = rand(12);
  const data = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, await deviceKey(), enc.encode(JSON.stringify(obj)));
  await db.kvSet(name, { iv: b64(iv), data: b64(data) });
}

export async function loadSecure(name) {
  const rec = await db.kvGet(name);
  if (!rec) return null;
  try {
    const buf = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: unb64(rec.iv) }, await deviceKey(), unb64(rec.data));
    return JSON.parse(new TextDecoder().decode(buf));
  } catch { return null; }
}
